import { useTheme, Theme } from '@mui/material/styles';
import React, { useMemo } from 'react';
import { INode, INodeProps } from '@/types/node';
import { getSpacerValue } from '@/lib/utils/format';
import { IContent } from './contract';
import { useNodeProps } from '@/node-editor/hooks';
import { Box } from '@mui/material';

interface INodeExtended extends Omit<INode, 'content'> {
  content: IContent;
}

interface IProps extends Omit<INodeProps, 'node'> {
  node: INodeExtended;
}

const useStyles = (
  theme: Theme,
  {
    alignment,
    verticalSpacing,
    override,
  }: Omit<IContent, 'html'> & { override: Record<string, any> }
) =>
  useMemo(
    () => ({
      embedContainer: {
        width: '100% !important',
        textAlign:
          alignment.horizontal === 'flex-start'
            ? 'left !important'
            : alignment.horizontal === 'flex-end'
            ? 'right !important'
            : alignment.horizontal,
        [`${theme.breakpoints.down('sm')}`]: {
          textAlign: 'center !important',
        },
        paddingTop: `${theme.spacing(
          getSpacerValue(verticalSpacing.top)
        )} !important`,
        paddingBottom: `${theme.spacing(
          getSpacerValue(verticalSpacing.bottom)
        )} !important`,
        whiteSpace: 'pre-wrap !important',
        '&:has(iframe), &:has(video)': {
          lineHeight: 0,
        },
        '& iframe, & video': {
          display: 'block !important',
          width: '100% !important',
        },
        ...override.root,
      },
    }),
    [
      theme,
      alignment.horizontal,
      verticalSpacing.top,
      verticalSpacing.bottom,
      override.root,
    ]
  );

/**
 * Embed Node description...
 */
export const Render = ({
  node: {
    id,
    override,
    content: { html, alignment, verticalSpacing },
  },
}: IProps) => {
  const theme = useTheme();
  const classes = useStyles(theme, { alignment, verticalSpacing, override });
  const nodeProps = useNodeProps(id);

  const htmlContent = useMemo(() => ({ __html: html }), [html]);

  return (
    <Box
      sx={classes.embedContainer}
      dangerouslySetInnerHTML={htmlContent}
      {...nodeProps}
    ></Box>
  );
};
